/** Hooks de dados para propostas (TanStack Query) + o motor de cálculo local. */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '@/lib/api';
import { calcProposta as calcPropostaRaw } from '@shared/tax.js';
import type {
  CreateProposalInput,
  Paged,
  Proposal,
  ProposalsQuery,
  ProposalListRow,
  TipoCalculo,
  UpdateProposalInput,
} from '@/lib/types';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ------------------------------------------------------------------ listagem --

export function useProposals(query: ProposalsQuery) {
  return useQuery({
    queryKey: ['proposals', query],
    queryFn: () =>
      get<Paged<ProposalListRow>>(
        `/proposals${buildQuery({
          q: query.q,
          uf: query.uf,
          status: query.status,
          userId: query.userId,
          from: query.from,
          to: query.to,
          page: query.page,
          perPage: query.perPage,
          sort: query.sort,
        })}`
      ),
    placeholderData: keepPreviousData,
  });
}

export function useProposal(id: number | undefined) {
  return useQuery({
    queryKey: ['proposal', id],
    queryFn: () => get<{ data: Proposal }>(`/proposals/${id}`).then((r) => r.data),
    enabled: id !== undefined,
  });
}

// -------------------------------------------------------------------- escrita --

export function useCreateProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProposalInput) =>
      post<{ data: Proposal }>('/proposals', input).then((r) => r.data),
    onSuccess: (proposal) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.setQueryData(['proposal', proposal.id], proposal);
    },
  });
}

export function useUpdateProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateProposalInput }) =>
      patch<{ data: Proposal }>(`/proposals/${id}`, input).then((r) => r.data),
    onSuccess: (proposal) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.setQueryData(['proposal', proposal.id], proposal);
    },
  });
}

export function useDeleteProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del<{ ok: true }>(`/proposals/${id}`),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.removeQueries({ queryKey: ['proposal', id] });
    },
  });
}

export interface DuplicateSkippedItem {
  productId: number | null;
  name: string;
  reason: string;
}

export interface DuplicateProposalResult {
  proposal: Proposal;
  skipped: DuplicateSkippedItem[];
}

export function useDuplicateProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      post<{ data: Proposal; skipped: DuplicateSkippedItem[] }>(`/proposals/${id}/duplicate`).then(
        (r): DuplicateProposalResult => ({ proposal: r.data, skipped: r.skipped })
      ),
    onSuccess: ({ proposal }) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      queryClient.setQueryData(['proposal', proposal.id], proposal);
    },
  });
}

// --------------------------------------------------------- cálculo local (tira) --

/**
 * `shared/tax.js` não tem tipos — este é o único ponto de tradução para TS
 * (mesma ideia de `lib/format.ts` para `shared/format.js`). NÃO reimplemente a
 * fórmula aqui; é sempre a mesma função que o servidor roda.
 */
export interface TaxItemInput {
  productId: number;
  unitValue: number;
  ipi: number;
  qty: number;
}

export interface TaxEstadoInput {
  alInter: number;
  alDest: number;
  fcp: number;
}

export interface TaxLinhaResult extends TaxItemInput {
  base: number;
  ipiValue: number;
  difal: number;
  fcp: number;
  lineTotal: number;
}

export interface TaxResult {
  linhas: TaxLinhaResult[];
  subtotal: number;
  totalIPI: number;
  totalDifal: number;
  totalFCP: number;
  totalGeral: number;
}

type CalcPropostaFn = (
  itens: TaxItemInput[],
  estado: TaxEstadoInput | null,
  tipo: TipoCalculo
) => TaxResult;

/** Calcula a proposta inteira localmente — mesmo motor que o servidor usa em `POST /calc` e `POST /proposals`. */
export const calcPropostaLocal: CalcPropostaFn = calcPropostaRaw as unknown as CalcPropostaFn;
